/**
 * Parse lyrics anchor markup from feedback content.
 *
 * Anchors allow feedback to reference specific lyrics text using the format:
 *   {@"text to highlight"}
 *
 * A single feedback item can have multiple anchors to reference multiple spots.
 *
 * This module re-exports parsing functions from @bandwith/lezer-feedback
 * which uses a proper Lezer grammar for robust parsing.
 */

import {
  createAnchorMarkup,
  hasLyricsAnchors,
  parseFeedback,
} from "@bandwith/lezer-feedback";

export { createAnchorMarkup, hasLyricsAnchors };

export type ParsedFeedbackAnchors = {
  /** Text snippets to anchor/highlight in the lyrics */
  anchors: string[];
  /** Feedback content with anchor markup stripped for display */
  displayContent: string;
};

/**
 * Parse anchor markup from feedback body text.
 *
 * @example
 * parseFeedbackAnchors('{@"always love you"} Try harmony here')
 * // => { anchors: ["always love you"], displayContent: "Try harmony here" }
 *
 * @example
 * parseFeedbackAnchors('{@"verse 1"} {@"verse 2"} Match these dynamics')
 * // => { anchors: ["verse 1", "verse 2"], displayContent: "Match these dynamics" }
 */
export function parseFeedbackAnchors(content: string): ParsedFeedbackAnchors {
  const result = parseFeedback(content);
  return {
    anchors: result.anchors,
    displayContent: result.displayContent,
  };
}
